import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { ROUTES } from '../../../../constants/RouteNameMapping';
import { KpiCollectionConfiguration } from '../../../../schemas/KpiCollectionConfiguration.schema';
import {
  KpiCollectionSettingsQueryKey,
  useCreateKpiCollectionConfiguration,
  useUpdateKpiCollectionConfiguration,
} from '../state/kpi-collection-state';
import { queryClient } from '../../../../services/MaggieQueryClient';

export function useSaveKpiCollectionConfig() {
  const navigate = useNavigate();
  const { handleSubmit, watch, reset } = useFormContext<KpiCollectionConfiguration>();
  const id = watch('id');

  const isUpdate = !!id;

  const { mutateAsync: create, isPending: isCreating } = useCreateKpiCollectionConfiguration();
  const { mutate: update, isPending: isUpdating } = useUpdateKpiCollectionConfiguration();

  const onSubmit = useCallback(
    async (data: KpiCollectionConfiguration) => {
      if (isUpdate) {
        update({ id, configuration: data }, { onSuccess: () => reset(data) });
      } else {
        const created = await create({ ...data, id: undefined });
        await queryClient.refetchQueries({ queryKey: KpiCollectionSettingsQueryKey });
        navigate(`/${ROUTES.KPI}/${ROUTES.KPI_DATA_COLLECTION_CONFIGURATION}/${created.id}`);
      }
    },
    [isUpdate, create, update, reset, navigate, id]
  );

  const save = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return {
    save,
    isUpdate,
    isPending: isCreating || isUpdating,
  };
}
